import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { useWanNames } from "./WanNames";
import { fmtBytes, truncate } from "../utils/formatters";

// ── Helpers ──────────────────────────────────────────────────────────────────

const hashCode = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

const STALE_SEC = 60;

export const getDeviceType = (device) => {
  const h = (device.hostname || "").toLowerCase();
  const m = (device.mac || "").toLowerCase();
  if (/iphone|android|phone|pixel|galaxy|oneplus|samsung/.test(h)) return "phone";
  if (/ipad|tablet/.test(h)) return "tablet";
  if (/macbook|laptop|thinkpad|dell|surface|notebook/.test(h)) return "laptop";
  if (/watch|fitbit|garmin/.test(h)) return "watch";
  if (/tv|roku|fire.?stick|chromecast|appletv|shield/.test(h)) return "tv";
  if (/echo|alexa|homepod|nest|sonos|speaker/.test(h)) return "speaker";
  if (/^eap\d|access.?point|unifi|omada|^ap[-_]|wifi|aruba|ruckus/.test(h)) return "ap";
  // MAC-based hinting
  if (/^(ac:de:48|00:17:88)/.test(m)) return "speaker"; // Philips Hue / Sonos
  return "device";
};

// ── Device icon paths (simple SVG) ──────────────────────────────────────────
// React.memo: type+size are primitive props that almost never change once
// a device is positioned, so the inner SVG tree can be reused across polls.

export const DeviceIcon = React.memo(function DeviceIcon({ type, size = 18 }) {
  const s = size;
  const common = {
    width: s, height: s, viewBox: "0 0 24 24",
    fill: "none", stroke: "currentColor", strokeWidth: "2",
    strokeLinecap: "round", strokeLinejoin: "round",
  };

  switch (type) {
    case "phone":
      return (
        <svg {...common}>
          <rect x="5" y="2" width="14" height="20" rx="3" />
          <line x1="12" y1="18" x2="12" y2="18.01" />
        </svg>
      );
    case "tablet":
      return (
        <svg {...common}>
          <rect x="3" y="2" width="18" height="20" rx="3" />
          <line x1="12" y1="18" x2="12" y2="18.01" />
        </svg>
      );
    case "laptop":
      return (
        <svg {...common}>
          <rect x="3" y="4" width="18" height="12" rx="2" />
          <line x1="2" y1="20" x2="22" y2="20" />
        </svg>
      );
    case "watch":
      return (
        <svg {...common}>
          <rect x="6" y="2" width="12" height="20" rx="4" />
          <circle cx="12" cy="12" r="4" />
        </svg>
      );
    case "tv":
      return (
        <svg {...common}>
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      );
    case "speaker":
      return (
        <svg {...common}>
          <rect x="4" y="2" width="16" height="20" rx="3" />
          <circle cx="12" cy="14" r="4" />
          <circle cx="12" cy="6" r="1" />
        </svg>
      );
    case "ap":
      return (
        <svg {...common}>
          <path d="M5 12.55a11 11 0 0 1 14.08 0" />
          <path d="M1.42 9a16 16 0 0 1 21.16 0" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <line x1="6" y1="10" x2="6" y2="10.01" />
          <line x1="10" y1="10" x2="10" y2="10.01" />
        </svg>
      );
  }
});

// ── Layout ───────────────────────────────────────────────────────────────────
//
// Devices sit on rings around the box, sized to how many there are: a ring
// holds as many as fit a label's width apart, and the next ring starts when it
// is full. Everything is placed in the drawing's own units against a device's
// footprint, the disc and the two lines under it, so no two devices meet and
// none covers the box in the middle. A window too narrow for the drawing gets
// it scaled down, never squeezed until the labels collide.

/* The drawing is never narrower than this, in its own units. */
const LAYOUT_MIN_W = 640;
/* The box that holds everything one device draws, around its centre: its disc
   while hovered, and its name and traffic underneath. */
export const FOOTPRINT = { left: 46, right: 46, top: 24, bottom: 48 };
/* Arc distance between neighbours on a ring: a label's width and a gap. */
const SLOT = 98;
/* Each ring steps out by more than a footprint's width at its sides and its
   height at its top and bottom, so two rings never touch. */
const RING_STEP = { a: 104, b: 80 };
const FIRST_RING = { a: 190, b: 108 };
const GAP = 6;
/* A name longer than this is cut, so it stays inside its footprint. */
const LABEL_CHARS = 14;
/* The box's internet lines, stacked to its left. */
const WAN_X = 60;
const WAN_STEP = 28;

/* What one device draws, relative to the box: its disc, then its name (at
   most LABEL_CHARS) and its traffic underneath. */
export const deviceShapes = (p) => ({
  disc: { x: p.x, y: p.y, r: 22 },
  boxes: [
    { l: p.x - 42, r: p.x + 42, t: p.y + 26, b: p.y + 37 },
    { l: p.x - 24, r: p.x + 24, t: p.y + 38, b: p.y + 47 },
  ],
});

/* What the box draws at the centre: its disc with the pulse round it, its
   name under it, and its lines stacked to its left with their names. */
export const hubShapes = (lines) => {
  const half = Math.max(0, lines - 1) * (WAN_STEP / 2) + 14;
  return [
    { l: -42, r: 42, t: -42, b: 42 },
    { l: -64, r: 64, t: 36, b: 50 },
    { l: -140, r: -WAN_X + 14, t: -half, b: half },
  ];
};

const boxesMeet = (a, b) => a.l < b.r + GAP && b.l < a.r + GAP && a.t < b.b + GAP && b.t < a.b + GAP;
const discMeetsBox = (c, b) =>
  Math.hypot(c.x - Math.max(b.l, Math.min(c.x, b.r)), c.y - Math.max(b.t, Math.min(c.y, b.b))) < c.r + GAP;
const shapesMeet = (s, t) =>
  Math.hypot(s.disc.x - t.disc.x, s.disc.y - t.disc.y) < s.disc.r + t.disc.r + GAP
  || s.boxes.some((a) => t.boxes.some((b) => boxesMeet(a, b)))
  || s.boxes.some((b) => discMeetsBox(t.disc, b))
  || t.boxes.some((b) => discMeetsBox(s.disc, b));
const meetsHub = (s, hub) => hub.some((b) => discMeetsBox(s.disc, b) || s.boxes.some((a) => boxesMeet(a, b)));

const perimeter = (a, b) => Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));

const STEPS = 720;
const dt = (2 * Math.PI) / STEPS;
// Distance along each ring at every step, by ring: the rings are a fixed
// series, so this holds a few dozen entries at most.
const ringLengths = new Map();

/* n points spaced evenly by distance along an ellipse, the first at its top,
   turned by `turn` of a spacing so neighbouring rings do not line up. */
function ringPoints(a, b, n, turn) {
  let along = ringLengths.get(`${a}x${b}`);
  if (!along) {
    along = [0];
    for (let i = 1; i <= STEPS; i++) {
      const t0 = -Math.PI / 2 + (i - 1) * dt;
      const t1 = t0 + dt;
      along.push(along[i - 1] + Math.hypot(a * (Math.cos(t1) - Math.cos(t0)), b * (Math.sin(t1) - Math.sin(t0))));
    }
    ringLengths.set(`${a}x${b}`, along);
  }
  const out = [];
  let j = 0;
  for (let k = 0; k < n; k++) {
    const s = ((k + turn) / n) * along[STEPS];
    while (j < STEPS - 1 && along[j + 1] < s) j++;
    const t = -Math.PI / 2 + (j + (s - along[j]) / (along[j + 1] - along[j] || 1)) * dt;
    // Whole units, as an arrangement is kept, so keeping one moves nothing.
    out.push({ x: Math.round(a * Math.cos(t)), y: Math.round(b * Math.sin(t)) });
  }
  return out;
}

const ringAt = (r) => ({ a: FIRST_RING.a + r * RING_STEP.a, b: FIRST_RING.b + r * RING_STEP.b });
const ringHolds = ({ a, b }) => Math.max(1, Math.floor(perimeter(a, b) / SLOT));

/* The free slots of one ring for `want` devices: as few slots as hold them,
   turned to step round whatever the box or a pinned device covers. */
function ringSlots(ring, want, turn, open) {
  let best = [];
  for (let n = Math.min(ringHolds(ring), want); n <= ringHolds(ring); n++) {
    for (const t of [0, 0.25, 0.5, 0.75]) {
      const slots = ringPoints(ring.a, ring.b, n, (turn + t) % 1).filter(open);
      if (Math.min(slots.length, want) > Math.min(best.length, want)) best = slots;
    }
    if (best.length >= want) break;
  }
  return best.slice(0, want);
}

/**
 * layoutDevices places devices around the box. keys is their order, active
 * first; pinned maps a key to [x, y] where an operator put that device,
 * relative to the box. Every other device takes the next ring slot that
 * neither a pinned device nor the box covers.
 *
 * The drawing's size follows the number of devices, not where they were put,
 * so arranging them never shrinks it under the operator's hand. Returns that
 * size in the drawing's own units, each device's place relative to the box,
 * and the rings the drawing shows as guides.
 */
export function layoutDevices(keys, { width = 800, lines = 0, pinned = {} } = {}) {
  const hub = hubShapes(lines);
  const at = {};
  const pinnedShapes = [];
  for (const k of keys) {
    const v = pinned[k];
    if (Array.isArray(v) && v.length === 2 && v.every(Number.isFinite)) {
      at[k] = { x: v[0], y: v[1] };
      pinnedShapes.push(deviceShapes(at[k]));
    }
  }
  const free = keys.filter((k) => !at[k]);
  const open = (p) => {
    const s = deviceShapes(p);
    return !meetsHub(s, hub) && !pinnedShapes.some((q) => shapesMeet(s, q));
  };

  let next = 0;
  for (let r = 0; next < free.length && r < 60; r++) {
    for (const p of ringSlots(ringAt(r), free.length - next, (r % 2) / 2, open)) {
      at[free[next++]] = p;
    }
  }

  // The rings every device needs when none is pinned: the guides, and the
  // least the drawing covers.
  const guides = [];
  for (let r = 0, held = 0; held < keys.length; r++) {
    guides.push(ringAt(r));
    held += ringHolds(guides[r]);
  }
  const outer = guides[guides.length - 1] || { a: 0, b: 0 };
  let halfW = Math.max(outer.a, ...hub.map((b) => Math.max(-b.l, b.r)));
  let up = Math.max(outer.b, ...hub.map((b) => -b.t));
  let down = Math.max(outer.b, ...hub.map((b) => b.b));
  for (const p of Object.values(at)) {
    halfW = Math.max(halfW, Math.abs(p.x));
    up = Math.max(up, -p.y);
    down = Math.max(down, p.y);
  }
  const margin = 16;
  return {
    width: Math.max(width, LAYOUT_MIN_W, 2 * (halfW + FOOTPRINT.right + margin)),
    height: 2 * Math.max(up + FOOTPRINT.top + margin, down + FOOTPRINT.bottom + margin),
    at,
    guides,
  };
}

/* A device is known by its hardware address, which survives a new lease, and
   by its address only when it has none. A second entry for one hardware
   address (a device that moved address) is told apart by the address. */
const deviceKeys = (devices) => {
  const seen = new Set();
  return devices.map((d, i) => {
    let k = d.mac || d.ip || `dev-${i}`;
    if (seen.has(k)) k = `${k}|${d.ip || i}`;
    seen.add(k);
    return k;
  });
};

/* The devices as the drawing and the phone's rows show them: active first,
   by address, then the idle ones, so the inner rings hold what is in use and
   the order never shuffles between polls; each with its key, name, kind and
   traffic. `known` holds every device's key, the hidden idle ones too. */
export function shownDevices(devices, hideStale) {
  const all = [...(devices || [])];
  all.sort((a, b) => {
    const ka = a.ip || a.mac || "";
    const kb = b.ip || b.mac || "";
    return ka.localeCompare(kb, undefined, { numeric: true });
  });
  const keys = deviceKeys(all);
  const decorated = all.map((d, i) => {
    const name = d.hostname && d.hostname !== "*" ? d.hostname : d.ip || "Unknown";
    return {
      ...d,
      _key: keys[i],
      _label: name,
      _type: getDeviceType(d),
      _isStale: (d.active_seconds ?? 0) > STALE_SEC,
      _totalBytes: (d.tx_bytes || 0) + (d.rx_bytes || 0),
    };
  });
  const active = decorated.filter((d) => !d._isStale);
  const idle = decorated.filter((d) => d._isStale);
  return { shown: hideStale ? active : [...active, ...idle], known: new Set(keys) };
}

/* The box's lines in name order, as the drawing stacks them. */
const sortedLines = (wans) => Object.values(wans || {}).sort((a, b) => (a.name || "").localeCompare(b.name || ""));

/* Where an operator put each device, per box, kept in this browser. It is a
   convenience: with storage refused the canvas lays itself out. */
const LAYOUT_KEY = "nabx.canvas.layout.v1";

function readLayout(node) {
  try {
    const pins = JSON.parse(localStorage.getItem(LAYOUT_KEY) || "{}")[node || "_"];
    return pins && typeof pins === "object" && !Array.isArray(pins) ? pins : {};
  } catch {
    return {};
  }
}

function writeLayout(node, pins) {
  try {
    const all = JSON.parse(localStorage.getItem(LAYOUT_KEY) || "{}");
    if (Object.keys(pins).length) all[node || "_"] = pins;
    else delete all[node || "_"];
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(all));
  } catch {
    /* the arrangement lasts until the page is left */
  }
}

/* How far a press may wander, in screen pixels, and still count as a click. */
const DRAG_START_PX = 5;

// DeviceNode renders one device circle + icon + label. Memoised on
// primitive props so React.memo's shallow compare actually skips rerenders
// when the polled diag payload is unchanged. Without this, NetworkCanvas
// re-creates and re-renders ~50 SVG sub-trees every diag tick (5s).
//
// The handlers are stable (useCallback in the parent) and take the device's
// key, so the inline arrows below do not bust the memo. A press is a click
// or the start of a drag; the parent tells them apart once the pointer moves.
const DeviceNode = React.memo(function DeviceNode({
  nodeKey,
  label,
  type,
  x,
  y,
  isStale,
  isHovered,
  isCarried,
  totalBytes,
  onEnter,
  onLeave,
  onPress,
  onOpen,
}) {
  const lit = isHovered || isCarried;
  const nodeR = lit ? 22 : 20;
  const fillColor = isStale ? "#1e293b" : "#0f172a";
  const strokeColor = isStale ? "#475569" : lit ? "#22d3ee" : "#6366f1";
  const iconColor = isStale ? "#64748b" : lit ? "#22d3ee" : "#a5b4fc";
  const labelColor = isStale ? "#475569" : lit ? "#e2e8f0" : "#94a3b8";

  return (
    <g
      className={`canvas-device-node${isCarried ? " is-carried" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={`${label}: open its details, or drag it to move it`}
      onPointerDown={(e) => onPress(e, nodeKey)}
      onMouseEnter={() => onEnter(nodeKey)}
      onMouseLeave={onLeave}
      onFocus={() => onEnter(nodeKey)}
      onBlur={onLeave}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(nodeKey);
        }
      }}
    >
      <circle cx={x} cy={y} r={nodeR + 8} fill="transparent" />
      <circle
        cx={x}
        cy={y}
        r={nodeR}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={lit ? 2.5 : 1.5}
        filter={lit ? "url(#glowSm)" : undefined}
        style={{ transition: "r 0.2s ease, stroke 0.25s ease, fill 0.25s ease" }}
      />
      <g
        transform={`translate(${x - 9}, ${y - 9})`}
        style={{ color: iconColor, transition: "color 0.25s ease" }}
      >
        <DeviceIcon type={type} size={18} />
      </g>
      <text
        x={x}
        y={y + nodeR + 14}
        textAnchor="middle"
        fill={labelColor}
        fontSize="9"
        fontWeight="600"
        style={{ transition: "fill 0.25s ease" }}
      >
        {label.length > LABEL_CHARS ? `${label.substring(0, LABEL_CHARS - 1)}…` : label}
      </text>
      {!isStale && totalBytes > 0 && (
        <text
          x={x}
          y={y + nodeR + 24}
          textAnchor="middle"
          fill="#64748b"
          fontSize="7.5"
          fontWeight="500"
        >
          {fmtBytes(totalBytes)}
        </text>
      )}
    </g>
  );
});

// ── Main Component ───────────────────────────────────────────────────────────

export default function NetworkCanvas({
  devices,
  selectedNode,
  onDeviceClick,
  wans,
  hideStale = false,
}) {
  const { wanName, wanColor } = useWanNames();
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  const [width, setWidth] = useState(800);

  // The width is the window's to decide; the height follows the drawing.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (w > 100) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Where the operator put devices on this box, and the one being carried.
  const [pins, setPins] = useState(() => readLayout(selectedNode));
  const [carried, setCarried] = useState(null); // { key, x, y } relative to the box
  const carryRef = useRef(null);
  useEffect(() => {
    setPins(readLayout(selectedNode));
    setCarried(null);
    carryRef.current = null;
  }, [selectedNode]);

  const { shown, known } = useMemo(() => shownDevices(devices, hideStale), [devices, hideStale]);
  const wanList = useMemo(() => sortedLines(wans), [wans]);

  const layout = useMemo(
    () => layoutDevices(shown.map((d) => d._key), { width, lines: wanList.length, pinned: pins }),
    [shown, width, wanList.length, pins]
  );

  // The drawing is laid out in its own units and scaled to the window; it is
  // never shorter on screen than a canvas worth looking at.
  const scale = width / layout.width;
  const minPx = width < 840 ? 280 : 460;
  const pixelH = Math.max(minPx, layout.height * scale);
  const viewW = layout.width;
  const viewH = pixelH / scale;
  const cx = viewW / 2;
  const cy = viewH / 2;

  const positioned = useMemo(
    () => shown.map((d) => {
      const rel = (carried && carried.key === d._key ? carried : layout.at[d._key]) || { x: 0, y: 0 };
      return { ...d, _x: cx + rel.x, _y: cy + rel.y };
    }),
    [shown, layout, carried, cx, cy]
  );

  // Handlers read the drawing through a ref, so they stay stable for the
  // memoised device nodes however often the poll re-renders.
  const view = useRef(null);
  view.current = { cx, cy, viewW, viewH, at: layout.at, shown, known, pins, selectedNode, onDeviceClick };

  // Max traffic for line thickness scaling
  const maxTraffic = useMemo(
    () => Math.max(1, ...positioned.map((d) => d._totalBytes)),
    [positioned]
  );

  const toDrawing = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    const m = svg && svg.getScreenCTM && svg.getScreenCTM();
    if (!m) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const p = pt.matrixTransform(m.inverse());
    return { x: p.x, y: p.y };
  }, []);

  const openDevice = useCallback((key) => {
    const v = view.current;
    const d = v.shown.find((s) => s._key === key);
    if (d && v.onDeviceClick) v.onDeviceClick(d);
  }, []);

  const pressDevice = useCallback((e, key) => {
    if (e.button !== 0) return;
    const p = toDrawing(e.clientX, e.clientY);
    const v = view.current;
    const rel = v.at[key];
    if (!p || !rel) return;
    // A press on a device is never the start of selecting the names around it.
    e.preventDefault();
    carryRef.current = {
      key, id: e.pointerId, sx: e.clientX, sy: e.clientY,
      dx: p.x - (v.cx + rel.x), dy: p.y - (v.cy + rel.y), moved: false, pos: rel,
    };
    svgRef.current.setPointerCapture?.(e.pointerId);
  }, [toDrawing]);

  const carry = useCallback((e) => {
    const c = carryRef.current;
    if (!c || c.id !== e.pointerId) return;
    if (!c.moved && Math.hypot(e.clientX - c.sx, e.clientY - c.sy) < DRAG_START_PX) return;
    const p = toDrawing(e.clientX, e.clientY);
    if (!p) return;
    const v = view.current;
    // Held inside the canvas, so a device can never be put where it cannot
    // be seen or picked up again.
    const x = Math.round(Math.min(v.viewW / 2 - FOOTPRINT.right - 4, Math.max(-v.viewW / 2 + FOOTPRINT.left + 4, p.x - c.dx - v.cx)));
    const y = Math.round(Math.min(v.viewH / 2 - FOOTPRINT.bottom - 4, Math.max(-v.viewH / 2 + FOOTPRINT.top + 4, p.y - c.dy - v.cy)));
    if (!c.moved) setHovered(null);
    c.moved = true;
    c.pos = { x, y };
    setCarried({ key: c.key, x, y });
  }, [toDrawing]);

  const drop = useCallback((e) => {
    const c = carryRef.current;
    if (!c || c.id !== e.pointerId) return;
    carryRef.current = null;
    svgRef.current?.releasePointerCapture?.(e.pointerId);
    setCarried(null);
    if (!c.moved) {
      openDevice(c.key);
      return;
    }
    // Moving one device keeps every other where it stands, rather than
    // sending them round their rings to close the gap it left. A device the
    // box no longer reports at all is forgotten.
    const v = view.current;
    const next = {};
    for (const [k, val] of Object.entries(v.pins)) if (v.known.has(k)) next[k] = val;
    for (const [k, rel] of Object.entries(v.at)) next[k] = [rel.x, rel.y];
    next[c.key] = [c.pos.x, c.pos.y];
    setPins(next);
    writeLayout(v.selectedNode, next);
  }, [openDevice]);

  const cancelCarry = useCallback(() => {
    carryRef.current = null;
    setCarried(null);
  }, []);

  const resetLayout = useCallback(() => {
    setPins({});
    writeLayout(view.current.selectedNode, {});
  }, []);

  const handleDeviceEnter = useCallback((key) => {
    if (!carryRef.current?.moved) setHovered(key);
  }, []);
  const handleDeviceLeave = useCallback(() => setHovered(null), []);

  const arranged = shown.some((d) => pins[d._key]);
  // The carried device draws last, so it passes over the others, not under.
  const drawOrder = carried
    ? [...positioned.filter((d) => d._key !== carried.key), ...positioned.filter((d) => d._key === carried.key)]
    : positioned;
  const reach = (layout.guides[layout.guides.length - 1]?.a || 160) + 40;

  return (
    <div className="canvas-container" ref={containerRef}>
      {shown.length > 0 && (
        <div className="canvas-tools">
          {arranged ? (
            <button type="button" className="canvas-reset" onClick={resetLayout}
                    title="Put every device back on the rings around the box">
              Reset layout
            </button>
          ) : (
            <span className="canvas-hint">Drag a device to move it</span>
          )}
        </div>
      )}
      <svg
        ref={svgRef}
        className={`canvas-svg${carried ? " is-carrying" : ""}`}
        viewBox={`0 0 ${viewW} ${viewH}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ height: pixelH }}
        onPointerMove={carry}
        onPointerUp={drop}
        onPointerCancel={cancelCarry}
        onLostPointerCapture={cancelCarry}
      >
        <defs>
          {/* Background gradient */}
          <radialGradient id="canvasBg" cx="50%" cy="50%" r="70%">
            <stop offset="0%" stopColor="#131B2E" />
            <stop offset="100%" stopColor="#0B1120" />
          </radialGradient>

          {/* Edge node gradient */}
          <radialGradient id="edgeGrad" cx="50%" cy="40%">
            <stop offset="0%" stopColor="#818CF8" />
            <stop offset="100%" stopColor="#4F46E5" />
          </radialGradient>

          {/* Glow filter */}
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="glowSm" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Pulse animation for edge node */}
          <filter id="pulse" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background */}
        <rect width={viewW} height={viewH} fill="url(#canvasBg)" rx="16" />

        {/* The rings the devices sit on */}
        {layout.guides.map((g, i) => (
          <ellipse
            key={i}
            cx={cx}
            cy={cy}
            rx={g.a}
            ry={g.b}
            fill="none"
            stroke="rgba(99, 102, 241, 0.07)"
            strokeWidth="1"
            strokeDasharray="4 8"
          />
        ))}

        {/* Crosshair lines */}
        <line
          x1={cx - reach}
          y1={cy}
          x2={cx + reach}
          y2={cy}
          stroke="rgba(99, 102, 241, 0.04)"
          strokeWidth="1"
        />
        <line
          x1={cx}
          y1={Math.max(0, cy - reach)}
          x2={cx}
          y2={Math.min(viewH, cy + reach)}
          stroke="rgba(99, 102, 241, 0.04)"
          strokeWidth="1"
        />

        {/* Connection lines from edge to devices. They follow a carried
            device as it moves, so nothing on them is animated. */}
        {positioned.map((d) => {
          const trafficRatio = d._totalBytes / maxTraffic;
          const opacity = d._isStale ? 0.08 : 0.12 + trafficRatio * 0.25;
          const lineWidth = d._isStale ? 1 : 1.5 + trafficRatio * 2;
          const isHov = hovered === d._key || (carried && carried.key === d._key);

          // Slight curve via quadratic bezier
          const mx = (cx + d._x) / 2;
          const my = (cy + d._y) / 2;
          const ddx = d._x - cx;
          const ddy = d._y - cy;
          const len = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
          const nx = -ddy / len;
          const ny = ddx / len;
          const curveOffset = (hashCode(d._key) % 20) - 10;
          const qx = mx + nx * curveOffset;
          const qy = my + ny * curveOffset;

          return (
            <path
              key={`line-${d._key}`}
              d={`M ${cx} ${cy} Q ${qx} ${qy} ${d._x} ${d._y}`}
              fill="none"
              stroke={
                d._isStale
                  ? "#475569"
                  : isHov
                  ? "#22d3ee"
                  : "#6366f1"
              }
              strokeWidth={isHov ? lineWidth + 1 : lineWidth}
              opacity={isHov ? opacity + 0.2 : opacity}
              strokeLinecap="round"
              style={{ transition: "stroke 0.25s ease, opacity 0.25s ease" }}
            />
          );
        })}

        {/* Edge node (center) */}
        <g className="canvas-edge-node">
          {/* Outer pulse ring */}
          <circle
            cx={cx}
            cy={cy}
            r={36}
            fill="none"
            stroke="#6366f1"
            strokeWidth="2"
            opacity="0.2"
            className="canvas-pulse-ring"
          />
          {/* Main circle */}
          <circle
            cx={cx}
            cy={cy}
            r={30}
            fill="url(#edgeGrad)"
            filter="url(#glow)"
            stroke="rgba(129, 140, 248, 0.4)"
            strokeWidth="2"
          />
          {/* Router icon */}
          <g transform={`translate(${cx - 10}, ${cy - 10})`}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="6" width="20" height="12" rx="2" />
              <line x1="6" y1="10" x2="6" y2="10.01" />
              <line x1="10" y1="10" x2="10" y2="10.01" />
              <line x1="14" y1="10" x2="14" y2="10.01" />
            </svg>
          </g>
          {/* Label */}
          <text
            x={cx}
            y={cy + 46}
            textAnchor="middle"
            fill="#e2e8f0"
            fontSize="11"
            fontWeight="700"
            letterSpacing="0.05em"
          >
            {truncate(selectedNode || "EDGE", 18)}
          </text>
        </g>

        {/* The box's internet lines, stacked to its left, each name beside
            its dot so no two names can land on each other or on the box's. */}
        {wanList.map((w, i) => {
          const wx = cx - WAN_X;
          const wy = cy + (i - (wanList.length - 1) / 2) * WAN_STEP;
          const cable = wanColor(w.name);
          return (
            <g key={`wan-${w.name}`}>
              {/* The cable's colour rings the link's up/down dot. The pale
                  edge keeps a black cable visible on the dark canvas. */}
              {cable && (
                <circle cx={wx} cy={wy} r={11.5} fill="none" stroke={cable.paint}
                        strokeWidth="3" style={{ filter: "drop-shadow(0 0 1px rgba(226, 232, 240, 0.9))" }} />
              )}
              <circle
                cx={wx}
                cy={wy}
                r={8}
                fill={w.active === false ? "#ef4444" : "#22c55e"}
                opacity="0.7"
              />
              <text
                x={wx - 16}
                y={wy + 3}
                textAnchor="end"
                fill="#94a3b8"
                fontSize="8"
                fontWeight="600"
              >
                <title>{w.name}</title>
                {truncate(wanName(w.name), 14)}
              </text>
            </g>
          );
        })}

        {/* Device nodes */}
        {drawOrder.map((d) => (
          <DeviceNode
            key={`dev-${d._key}`}
            nodeKey={d._key}
            label={d._label}
            type={d._type}
            x={d._x}
            y={d._y}
            isStale={d._isStale}
            isHovered={hovered === d._key}
            isCarried={!!carried && carried.key === d._key}
            totalBytes={d._totalBytes}
            onEnter={handleDeviceEnter}
            onLeave={handleDeviceLeave}
            onPress={pressDevice}
            onOpen={openDevice}
          />
        ))}

        {/* Hover tooltip */}
        {hovered && !carried && (() => {
          const d = positioned.find((p) => p._key === hovered);
          if (!d) return null;
          const tw = 160;
          const th = 56;
          let tx = d._x + 28;
          let ty = d._y - th / 2;
          if (tx + tw > viewW - 10) tx = d._x - tw - 28;
          if (ty < 10) ty = 10;
          if (ty + th > viewH - 10) ty = viewH - th - 10;

          return (
            <g className="canvas-tooltip">
              <rect
                x={tx}
                y={ty}
                width={tw}
                height={th}
                rx="8"
                fill="rgba(15, 23, 42, 0.92)"
                stroke="rgba(99, 102, 241, 0.3)"
                strokeWidth="1"
              />
              <text x={tx + 10} y={ty + 16} fill="#e2e8f0" fontSize="10" fontWeight="700">
                {truncate(d._label, 26)}
              </text>
              <text x={tx + 10} y={ty + 30} fill="#94a3b8" fontSize="9">
                ↑ {fmtBytes(d.tx_bytes)} · ↓ {fmtBytes(d.rx_bytes)}
              </text>
              <text x={tx + 10} y={ty + 44} fill="#64748b" fontSize="8.5">
                {d.mac || "—"} · {d._type}
              </text>
            </g>
          );
        })()}

        {/* Empty state */}
        {positioned.length === 0 && (
          <g>
            <text
              x={cx}
              y={cy + 70}
              textAnchor="middle"
              fill="#475569"
              fontSize="13"
              fontWeight="500"
            >
              No devices connected
            </text>
            <text
              x={cx}
              y={cy + 86}
              textAnchor="middle"
              fill="#334155"
              fontSize="11"
            >
              Devices will appear here as they connect to the edge
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

/* The phone layout's drawing of the same network: the box's lines, then its
   devices, a row each in the order the drawing places them. A line is green
   while up and red when down, as on the drawing; pressing a device opens its
   details. */
export function NetworkRows({ devices, wans, onDeviceClick, hideStale = false }) {
  const { wanName } = useWanNames();
  const { shown } = useMemo(() => shownDevices(devices, hideStale), [devices, hideStale]);
  const lines = useMemo(() => sortedLines(wans), [wans]);
  return (
    <div className="net-rows">
      {lines.length > 0 && (
        <ul className="net-lines" aria-label="Internet lines">
          {lines.map((w) => (
            <li key={w.name} className={`net-line${w.active === false ? " is-down" : ""}`}>
              <span className="net-dot" aria-hidden="true" />
              <span className="net-line-name">{wanName(w.name)}</span>
              <span className="net-line-use">
                {w.active === false ? "Down" : `↑ ${fmtBytes(w.tx_bytes)}  ↓ ${fmtBytes(w.rx_bytes)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
      {shown.length === 0 ? (
        <p className="net-empty">No devices connected. They appear here as they join the box's network.</p>
      ) : (
        <ul className="net-devices" aria-label="Devices">
          {shown.map((d) => (
            <li key={d._key}>
              <button type="button" className={`net-device${d._isStale ? " is-idle" : ""}`} onClick={() => onDeviceClick?.(d)}>
                <span className="net-device-icon"><DeviceIcon type={d._type} size={18} /></span>
                <span className="net-device-text">
                  <span className="net-device-name">{d._label}</span>
                  <span className="net-device-sub">{d._label === d.ip ? "" : `${d.ip} · `}{d._isStale ? "idle" : "active"}</span>
                </span>
                <span className="net-device-use">{d._totalBytes > 0 ? fmtBytes(d._totalBytes) : ""}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
